import { auth } from '../firebase'

export interface ChatTurn {
  role: 'user' | 'assistant'
  content: string
}

/** Mirrors server/src/services/chat/tools.ts's create_emergency_request fields. */
export interface BookingDraft {
  postcode: string
  category: string
  issueDescription: string
  phone: string
  email?: string
  address?: string
  name?: string
}

/** Mirrors server/src/services/chat/tools.ts's find_plumbers_nearby result
 *  shape exactly - deliberately never includes phone/email, the server never
 *  even selects them out of the database for this tool in the first place. */
export interface PlumberResultCard {
  companyName: string
  directorySlug: string
  city?: string
  postcodeArea?: string
  googleRating?: number
  googleReviewCount?: number
  gasSafe: boolean
  availableNow: boolean
}

export type ChatStreamEvent =
  | { type: 'text'; text: string }
  | { type: 'confirm_booking'; data: BookingDraft }
  | { type: 'category_picker' }
  | { type: 'plumber_results'; data: PlumberResultCard[] }
  | { type: 'human_support_requested'; data: { ticketRef: string } }
  | { type: 'chat_escalated' }
  | { type: 'error'; message: string }

/**
 * Streams a chat reply, invoking onEvent for each newline-delimited JSON
 * event as it arrives (see routes/chat.ts for the wire protocol - plain text
 * deltas plus structured events like confirm_booking).
 *
 * Uses plain fetch rather than the shared `api` axios client (lib/apiClient.ts)
 * - axios doesn't expose the response body as a readable stream in the
 *   browser the way fetch does, and streaming is the entire point here.
 * Still attaches the Firebase ID token when a customer happens to be signed
 * in, matching apiClient's interceptor - but works perfectly anonymously too,
 * same as the rest of the emergency-job flow.
 */
export async function streamChatMessage(
  messages: ChatTurn[],
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
  conversationId?: string,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const user = auth?.currentUser
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`
  // One per chat session (see useChatSession.ts) - lets the server log every
  // turn of this conversation into the same Discord thread (issue #50).
  if (conversationId) headers['X-Chat-Conversation-Id'] = conversationId

  const res = await fetch('/api/chat/message', {
    method: 'POST',
    headers,
    body: JSON.stringify({ messages }),
    signal,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null
    throw new Error(body?.error ?? 'Chat request failed')
  }
  if (!res.body) throw new Error('Streaming is not supported in this browser')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? '' // last (possibly incomplete) line carries over
    for (const line of lines) {
      if (!line.trim()) continue
      try {
        onEvent(JSON.parse(line) as ChatStreamEvent)
      } catch {
        // Malformed/split line - drop it, matching the server's own
        // defensive SSE parsing rather than failing the whole conversation.
      }
    }
  }
}

/**
 * Polled by useChatSession.ts once escalate_stuck has fired (issue #51),
 * for as long as the chat widget stays open - picks up a human's reply
 * posted directly in this conversation's Discord thread. `since` is a plain
 * count of replies already rendered (replies are only ever appended server-
 * side, never reordered/removed, so a count is a safe, simple cursor).
 */
export async function fetchChatReplies(
  conversationId: string,
  since: number,
): Promise<{ replies: { text: string; at: string }[]; resolved: boolean }> {
  const res = await fetch(`/api/chat/replies?since=${since}`, {
    headers: { 'X-Chat-Conversation-Id': conversationId },
  })
  if (!res.ok) throw new Error('Failed to fetch replies')
  return res.json()
}
